import type { ConsentState } from '../hooks/useCookieConsent'

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
const PLAUSIBLE_DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined

const GA_SCRIPT_ID = 'essencia-ga'
const PLAUSIBLE_SCRIPT_ID = 'essencia-plausible'

const ALLOWED_EVENTS = new Set([
  'page_view',
  'signup_start',
  'signup_complete',
])

let analyticsEnabled = false

function removeElement(id: string) {
  document.getElementById(id)?.remove()
}

function loadGa(id: string) {
  if (document.getElementById(GA_SCRIPT_ID)) return

  const src = document.createElement('script')
  src.id = GA_SCRIPT_ID
  src.async = true
  src.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  document.head.appendChild(src)

  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args)
  }
  window.gtag('js', new Date())
  window.gtag('config', id, { anonymize_ip: true })
}

function disableGa(id: string) {
  removeElement(GA_SCRIPT_ID)
  ;(window as unknown as Record<string, boolean>)[`ga-disable-${id}`] = true
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0]?.trim()
    if (name && /^(_ga|_gid|_gat)/.test(name)) {
      document.cookie = `${name}=; Max-Age=0; path=/`
    }
  })
}

function loadPlausible(domain: string) {
  if (document.getElementById(PLAUSIBLE_SCRIPT_ID)) return
  const s = document.createElement('script')
  s.id = PLAUSIBLE_SCRIPT_ID
  s.defer = true
  s.dataset.domain = domain
  s.src = 'https://plausible.io/js/script.js'
  document.head.appendChild(s)
}

function disablePlausible() {
  removeElement(PLAUSIBLE_SCRIPT_ID)
}

export function enableAnalytics() {
  analyticsEnabled = true
  if (GA_ID) loadGa(GA_ID)
  if (PLAUSIBLE_DOMAIN) loadPlausible(PLAUSIBLE_DOMAIN)
}

export function disableAnalytics() {
  analyticsEnabled = false
  if (GA_ID) disableGa(GA_ID)
  disablePlausible()
}

export function trackEvent(name: string, props?: Record<string, string | number | boolean>) {
  if (!analyticsEnabled) return
  if (!ALLOWED_EVENTS.has(name)) return
  if (props && ('patient_id' in props || 'note_id' in props || 'raw_notes' in props)) return

  if (window.gtag && GA_ID) {
    window.gtag('event', name, props)
  }
}

export function applyScriptConsent(consent: ConsentState) {
  if (consent.analytics) enableAnalytics()
  else disableAnalytics()
}

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}
